import React, { useCallback } from "react";
import { Dropdown, DropdownBody, DropdownBodyProps, DropdownHover, DropdownHoverProps } from "../../layout";
import { LinkButton, LinkButtonProps } from "../../misc";
import * as WebRoutes from "@travellocal/utils/webRoutes";
import { ContentCard } from "../ContentCard/ContentCard";
import "./InspirationDropdown.scss";

interface InspirationDropdownProps {
  content?: [IContent, IContent, IContent];
}

const InspirationHoverLink: React.FC<DropdownHoverProps> = (props) => {
  return (
    <DropdownHover {...props} mouseOnly>
      <LinkButton
        to={WebRoutes.inspiration()}
        buttonClass="secondary-button"
        className="heading--4 is-marginless is-paddingless"
      >
        Inspiration
      </LinkButton>
    </DropdownHover>
  );
};

const inspirationLinkProps = (contentType: ContentType): LinkButtonProps => ({
  to: WebRoutes.inspiration({ types: [ contentType] }, 1, true),
  className: "heading--4 text--dark is-marginless",
  buttonClass: "secondary-button",
});

const InspirationDropdownBody: React.FC<DropdownBodyProps & InspirationDropdownProps> = (props) => {
  const tabIndex = props.isVisible ? 0 : -1; // If visible, allow tabbing through links

  const [itinerary, blog, review] = props.content ?? [null, null, null];

  return (
    <DropdownBody {...props} className="inspiration-dropdown__body">
      <div className="columns is-marginless">
        <div className="column is-4-tablet is-3-desktop inspiration-dropdown__info">
          <h3>Inspiration</h3>
          <p>
            Have a look through trip ideas, articles and reviews{" "}
            from our previous travellers to inspire your holiday.
          </p>

          <LinkButton
            to={WebRoutes.inspiration()}
            buttonClass="primary-button"
            className="heading--4 text--light"
            tabIndex={tabIndex}>
            Get Inspired
          </LinkButton>
        </div>

        <div className="column inspiration-dropdown__cards">
          <div className="columns is-marginless">
            <div className="column inspiration-dropdown__card">
              <ContentCard content={itinerary} className="content-card-minimal" tabIndex={tabIndex} />
            </div>

            <div className="column inspiration-dropdown__card">
              <ContentCard content={blog} className="content-card-minimal" tabIndex={tabIndex} />
            </div>

            <div className="column inspiration-dropdown__card is-hidden-tablet-only">
              <ContentCard content={review} className="content-card-minimal" tabIndex={tabIndex} />
            </div>
          </div>

          <div className="columns is-marginless">
            <div className="column has-text-centered">
              <LinkButton {...inspirationLinkProps(ContentType.SampleItinerary)} tabIndex={tabIndex}>
                More trip ideas
              </LinkButton>
            </div>

            <div className="column has-text-centered">
              <LinkButton {...inspirationLinkProps(ContentType.BlogPost)} tabIndex={tabIndex}>
                More articles
              </LinkButton>
            </div>

            <div className="column has-text-centered is-hidden-tablet-only">
              <LinkButton {...inspirationLinkProps(ContentType.Review)} tabIndex={tabIndex}>
                More reviews
              </LinkButton>
            </div>
          </div>

        </div>
      </div>
    </DropdownBody>
  );
}

/**
 * Inspiration dropdown on the marketing site.
 * 
 * @status development
 */
export const InspirationDropdown: React.FC<InspirationDropdownProps> = (props) => {
  const renderBody = useCallback(bodyProps => <InspirationDropdownBody {...bodyProps} {...props} />, [props.content]);
  return (
    <Dropdown
      className="inspiration-dropdown"
      Hover={InspirationHoverLink}
      Body={renderBody}
    />
  );
}
